import React, { useState, useEffect, useRef } from 'react';
import { ImageOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SmartImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src: string;
    alt: string;
    blurHash?: string;
    fallbackSrc?: string;
    aspectRatio?: string; // e.g. "aspect-[4/3]"
    containerClassName?: string;
    priority?: boolean;
    fetchPriority?: "high" | "low" | "auto";
    loading?: "lazy" | "eager";
    decoding?: "async" | "auto" | "sync";
    seoContext?: {
        title?: string;
        category?: string;
        location?: string;
        index?: number;
    };
    fallbackText?: string;
}

export const SmartImage: React.FC<SmartImageProps> = ({
    src: initialSrc,
    alt: providedAlt,
    blurHash,
    fallbackSrc,
    aspectRatio,
    containerClassName = '',
    className = '',
    priority = false,
    fetchPriority: customFetchPriority,
    loading: customLoading,
    decoding: customDecoding,
    seoContext,
    fallbackText,
    ...props
}) => {
    const { t } = useTranslation('listing');
    
    // Generate smart SEO alt if provided alt is empty or generic
    const alt = React.useMemo(() => {
        if (providedAlt && providedAlt.length > 5 && !providedAlt.toLowerCase().includes('foto')) {
            return providedAlt;
        }
        
        if (seoContext) {
            const parts = [];
            if (seoContext.title) parts.push(seoContext.title);
            if (seoContext.category) parts.push(seoContext.category);
            if (seoContext.location) parts.push(seoContext.location.split(',')[0].trim()); // Just city
            if (typeof seoContext.index === 'number') parts.push(`${providedAlt || 'Foto'} ${seoContext.index + 1}`);
            
            if (parts.length > 0) {
                return parts.join(' | ') + ' | Espacero';
            }
        }
        
        return providedAlt || `${t('listing:default_alt')} | Espacero`;
    }, [providedAlt, seoContext, t]);

    // Resolve relative R2 paths and upgrade HTTP to HTTPS
    const originalSrc = React.useMemo(() => {
        if (!initialSrc) return '';
        if (initialSrc.startsWith('http://')) {
            return initialSrc.replace('http://', 'https://');
        }
        if (initialSrc.startsWith('https://') || initialSrc.startsWith('data:') || initialSrc.startsWith('/') || initialSrc.startsWith('blob:')) {
            return initialSrc;
        }
        // It's a relative path (e.g. "listing-images/...")
        // Resolve it to the R2 upload proxy URL
        const r2Base = (import.meta.env.VITE_R2_WORKER_URL as string) || 'https://upload.espacero.com';
        return `${r2Base.replace(/\/+$/, '')}/file/${initialSrc}`;
    }, [initialSrc]);

    // Local blog assets ship prebuilt .webp siblings (~60 % smaller than the
    // committed PNGs). Serve the .webp first; handleError falls back to the
    // original file if the sibling is ever missing.
    const webpSibling = React.useMemo(() => {
        const normalized = originalSrc.startsWith('/') ? originalSrc : '';
        return /^\/blog-[^/]+\.(png|jpe?g)$/i.test(normalized)
            ? normalized.replace(/\.(png|jpe?g)$/i, '.webp')
            : null;
    }, [originalSrc]);

    const src = webpSibling || originalSrc;

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [currentSrc, setCurrentSrc] = useState(src);
    const [isUsingFallback, setIsUsingFallback] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    // External venue photos (public-source listings) live on arbitrary
    // domains and used to ship as multi-MB originals — /market/ weighed
    // ~10 MB (9th audit P0). Route them through the R2 worker's /ext/
    // Image-Resizing proxy; if the proxy ever fails for a given image we
    // fall back to the raw origin URL (proxyDisabled) so nothing breaks.
    const EXT_PROXY_BASE = 'https://upload.espacero.com/ext/';
    const [proxyDisabled, setProxyDisabled] = useState(false);
    const isProxyableExternal = (url?: string) =>
        !!url &&
        /^https?:\/\//i.test(url) &&
        !url.includes('espacero.com') &&
        !url.includes('unsplash.com') &&
        !url.includes('.pages.dev') &&
        !url.startsWith(EXT_PROXY_BASE);

    // Covers both images.unsplash.com and plus.unsplash.com (premium photos)
    const isOptimizable = (url?: string) => !!url && (
        url.includes('upload.espacero.com') ||
        url.includes('unsplash.com') ||
        (!proxyDisabled && isProxyableExternal(url))
    );

    const getOptimizedSrc = (url: string, width: number) => {
        if (!url) return url;
        try {
            if (url.includes('upload.espacero.com')) {
                const urlObj = new URL(url);
                urlObj.searchParams.set('width', width.toString());
                urlObj.searchParams.set('quality', '80');
                urlObj.searchParams.set('format', 'auto');
                return urlObj.toString();
            }
            if (url.includes('unsplash.com')) {
                const urlObj = new URL(url);
                urlObj.searchParams.set('w', width.toString());
                urlObj.searchParams.set('q', '75');
                urlObj.searchParams.set('auto', 'format');
                return urlObj.toString();
            }
            if (!proxyDisabled && isProxyableExternal(url)) {
                return `${EXT_PROXY_BASE}${encodeURIComponent(url)}?width=${width}&quality=75&format=auto`;
            }
        } catch (e) {
            // ignore parsing errors
        }
        return url;
    };

    // 200w exists for tiny grid thumbnails (compact cards are 80–96px on
    // mobile); callers pass a matching `sizes` prop to opt in.
    const finalSrcSet = isOptimizable(currentSrc)
        ? `${getOptimizedSrc(currentSrc, 200)} 200w, ${getOptimizedSrc(currentSrc, 400)} 400w, ${getOptimizedSrc(currentSrc, 800)} 800w, ${getOptimizedSrc(currentSrc, 1200)} 1200w`
        : undefined;

    const finalSizes = isOptimizable(currentSrc)
        ? "(max-width: 640px) 400px, (max-width: 1024px) 800px, 1200px"
        : undefined;

    // Reset error and loading states when src changes
    useEffect(() => {
        setIsLoading(true);
        setHasError(false);
        setCurrentSrc(src);
        setIsUsingFallback(false);
        setProxyDisabled(false);
    }, [src]);

    // Check if image is already cached and loaded
    useEffect(() => {
        if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
            setIsLoading(false);
        }
    }, [currentSrc]);

    // Handle BlurHash rendering
    useEffect(() => {
        if (isLoading && blurHash && canvasRef.current) {
            import('../utils/blurhash').then(({ decodeBlurHash }) => {
                const canvas = canvasRef.current;
                if (!canvas) return;
                const ctx = canvas.getContext('2d');
                if (!ctx) return;

                const pixels = decodeBlurHash(blurHash, 32, 32);
                const imageData = new ImageData(pixels as any, 32, 32);
                ctx.putImageData(imageData, 0, 0);
            }).catch(err => {
                console.error('[SmartImage] BlurHash decode failed:', err);
            });
        }
    }, [isLoading, blurHash]);

    const handleLoad = () => {
        setIsLoading(false);
    };

    const handleError = () => {
        // Proxied external image failed (origin blocks Cloudflare, dead URL
        // through resizer, …) → retry once with the raw origin URL.
        if (!proxyDisabled && isProxyableExternal(currentSrc)) {
            setProxyDisabled(true);
            setIsLoading(true);
            return;
        }
        // .webp sibling missing → retry with the original committed file
        if (webpSibling && currentSrc === webpSibling && originalSrc !== webpSibling) {
            setCurrentSrc(originalSrc);
            setIsLoading(true);
            return;
        }
        if (fallbackSrc && !isUsingFallback) {
            setCurrentSrc(fallbackSrc);
            setIsUsingFallback(true);
            setIsLoading(true);
            return;
        }

        setIsLoading(false);
        
        // If it's our own placeholder failing, or the legacy fallback, we don't want to show the "OBRÁZOK NEDOSTUPNÝ" text
        const isPlaceholder = currentSrc?.includes('external-gallery-placeholder') || 
                            (currentSrc?.includes('images.unsplash.com') && currentSrc?.includes('1519741497674-611481863552'));
        
        if (!isPlaceholder) {
            setHasError(true);
        }
    };

    const finalLoading = customLoading || (priority ? "eager" : "lazy");
    const finalDecoding = customDecoding || "async";
    const finalFetchPriority = customFetchPriority || (priority ? "high" : "auto");

    return (
        <div className={`relative overflow-hidden pointer-events-none bg-stone-100 dark:bg-stone-800 ${aspectRatio || ''} ${containerClassName}`}>

            {/* BlurHash Background / Loader */}
            {isLoading && (
                <div className="absolute inset-0 z-10 bg-stone-100 dark:bg-stone-800">
                    {blurHash && (
                        <canvas
                            ref={canvasRef}
                            width={32}
                            height={32}
                            className="w-full h-full object-cover blur-md scale-110"
                        />
                    )}
                    {!priority && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-8 h-8 border-2 border-stone-200/50 dark:border-stone-700/50 border-t-accent-terra dark:border-t-accent-terra rounded-full animate-spin"></div>
                        </div>
                    )}
                </div>
            )}

            {/* Error State */}
            {hasError ? (
                fallbackText ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-stone-900 p-6 text-center">
                        <div className="absolute inset-0 bg-gradient-to-br from-stone-800/50 to-stone-950/80 pointer-events-none" />
                        <h3 className="relative z-10 text-stone-300 font-medium text-lg leading-snug max-w-[90%] opacity-80" style={{ textWrap: 'balance' }}>
                            {fallbackText}
                        </h3>
                    </div>
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-stone-50 dark:bg-stone-900 text-stone-400 dark:text-stone-600">
                        <ImageOff size={24} className="mb-2 opacity-50" />
                        <span className="text-[10px] font-medium uppercase tracking-wider">{t('image_unavailable')}</span>
                    </div>
                )
            ) : (
                <img
                    ref={imgRef}
                    src={isOptimizable(currentSrc) ? getOptimizedSrc(currentSrc, 800) : currentSrc}
                    srcSet={finalSrcSet}
                    sizes={finalSizes}
                    alt={alt}
                    loading={finalLoading}
                    decoding={finalDecoding}
                    fetchPriority={finalFetchPriority}
                    onLoad={handleLoad}
                    onError={handleError}
                    className={`
            transition-opacity duration-500 ease-out pointer-events-auto
            ${isLoading ? 'opacity-0' : 'opacity-100'}
            ${className}
          `}
                    {...props}
                />
            )}
        </div>
    );
};

